import { useState, useEffect, useCallback } from 'react';
import CreateSessionForm from '../components/CreateSessionForm';
import SessionList from '../components/SessionList';
import TopNav from '../components/TopNav';
import {
  api,
  setAuthTokenProvider,
  type CreateSessionResponse,
  type SessionResult,
} from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { detectProvider } from '../utils/auth-utils';

const PAGE_SIZE = 20;

export default function RecruiterDashboard() {
  const { user, signOut, getAccessToken, isAdmin } = useAuth();
  const [sessions, setSessions] = useState<SessionResult[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [newLink, setNewLink] = useState<CreateSessionResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setAuthTokenProvider(getAccessToken);
  }, [getAccessToken]);

  const loadSessions = useCallback(async () => {
    setLoadError(null);
    try {
      const data = await api.listSessions(PAGE_SIZE);
      setSessions(data.sessions);
      setNextCursor(data.nextCursor);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  }, []);

  async function loadMore() {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const data = await api.listSessions(PAGE_SIZE, nextCursor);
      setSessions((prev) => [...prev, ...data.sessions]);
      setNextCursor(data.nextCursor);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  }

  useEffect(() => {
    void loadSessions();
  }, [loadSessions]);
  useEffect(() => {
    const interval = setInterval(() => void loadSessions(), 30_000);
    return () => clearInterval(interval);
  }, [loadSessions]);

  function handleCreated(result: CreateSessionResponse) {
    setNewLink(result);
    setCopied(false);
    void loadSessions();
  }

  function handleCopy(url: string) {
    void navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const provider = detectProvider(user?.username);
  const authEmail = useAuth().email;
  const email = authEmail || user?.signInDetails?.loginId || '';

  const pendingCount = sessions.filter((s) => s.status === 'PENDING').length;
  const passedCount = sessions.filter((s) => s.passed).length;
  const failedCount = sessions.filter(
    (s) => (s.status === 'SUCCEEDED' && !s.passed) || s.status === 'FAILED',
  ).length;

  return (
    <>
      <TopNav
        email={email}
        provider={provider}
        isAdmin={isAdmin}
        onSignOut={() => void signOut()}
      />

      <div className="dashboard">
        <h1 className="dashboard-title">Dashboard</h1>
        <p className="dashboard-subtitle">Create liveness check links and monitor results.</p>

        {sessions.length > 0 && (
          <div className="stats-row">
            <div className="stat-card stat-card-total">
              <div className="stat-number">{sessions.length}</div>
              <div className="stat-label">Total</div>
            </div>
            <div className="stat-card stat-card-pending">
              <div className="stat-number">{pendingCount}</div>
              <div className="stat-label">Pending</div>
            </div>
            <div className="stat-card stat-card-pass">
              <div className="stat-number">{passedCount}</div>
              <div className="stat-label">Passed</div>
            </div>
            <div className="stat-card stat-card-fail">
              <div className="stat-number">{failedCount}</div>
              <div className="stat-label">Failed</div>
            </div>
          </div>
        )}

        <div className="card create-card">
          <CreateSessionForm onCreated={handleCreated} />
        </div>

        {newLink && (
          <div className="link-banner">
            <button className="btn-icon link-banner-close" onClick={() => setNewLink(null)}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
            <div className="link-banner-title">Check link ready</div>
            <div className="link-banner-desc">
              Share this link with the candidate. It expires in 7 days.
            </div>
            <div className="link-banner-url">
              <code>{newLink.checkUrl}</code>
              <button
                className={`btn-copy ${copied ? 'copied' : ''}`}
                onClick={() => handleCopy(newLink.checkUrl)}
              >
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>
        )}

        {loadError && <p className="error-msg">{loadError}</p>}

        {loading ? (
          <div className="card sessions-card">
            <div className="sessions-header">
              <h2>Sessions</h2>
            </div>
            {[1, 2, 3, 4, 5].map((i) => (
              <div className="skeleton-row" key={i}>
                <div className="skeleton" style={{ width: '30%', height: 16 }} />
                <div
                  className="skeleton"
                  style={{ width: '15%', height: 24, borderRadius: 9999 }}
                />
                <div className="skeleton" style={{ width: '10%', height: 16 }} />
                <div className="skeleton" style={{ width: '15%', height: 16 }} />
              </div>
            ))}
          </div>
        ) : (
          <>
            <SessionList sessions={sessions} onRefresh={() => void loadSessions()} />
            {nextCursor && (
              <div className="pagination">
                <button
                  className="btn btn-ghost"
                  onClick={() => void loadMore()}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Loading...' : 'Load more'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
